/**
 * Story API Service
 *
 * Single interface for all story-related operations.
 * Uses the new /api/story endpoints.
 */
import api from './api';

/**
 * Get story configuration for an event type
 * @param {string} eventType - 'new_vehicle', 'price_reduction', 'sold_vehicle'
 * @returns {Promise<Object>} Resolved configuration
 */
export const getStoryConfig = async (eventType = 'new_vehicle') => {
  const response = await api.get(`/story/config/${eventType}`);
  return response.data;
};

/**
 * Update story configuration for an event type
 * @param {string} eventType - 'new_vehicle', 'price_reduction', 'sold_vehicle'
 * @param {Object} config - Configuration updates
 * @returns {Promise<Object>} Updated configuration
 */
export const updateStoryConfig = async (eventType, config) => {
  const response = await api.put(`/story/config/${eventType}`, config);
  return response.data;
};

/**
 * Get all story configurations
 * @returns {Promise<Object>} All configurations by event type
 */
export const getAllStoryConfigs = async () => {
  const response = await api.get('/story/configs');
  return response.data;
};

/**
 * Get available templates and event types
 * @returns {Promise<Object>} { templates: [], event_types: [] }
 */
export const getStoryTemplates = async () => {
  const response = await api.get('/story/templates');
  return response.data;
};

/**
 * Generate story preview
 * @param {string} vehicleId - Vehicle ID
 * @param {string} eventType - Event type
 * @returns {Promise<Object>} { preview_url, config }
 */
export const generateStoryPreview = async (vehicleId, eventType = 'new_vehicle') => {
  const response = await api.post('/story/preview', {
    vehicle_id: vehicleId,
    event_type: eventType,
  });
  return response.data;
};

/**
 * Generate demo preview for Settings page
 * Uses a demo car image with user's current config
 * @param {string} eventType - Event type
 * @returns {Promise<Object>} { preview_url, config }
 */
export const generateDemoPreview = async (eventType = 'new_vehicle') => {
  const response = await api.get(`/story/preview-demo/${eventType}`);
  return response.data;
};

/**
 * Publish story
 * @param {string} vehicleId - Vehicle ID
 * @param {string} eventType - Event type
 * @param {string} platform - 'instagram' or 'facebook'
 * @param {string} caption - Optional caption
 * @returns {Promise<Object>} Publication result
 */
export const publishStory = async (vehicleId, eventType = 'new_vehicle', platform = 'instagram', caption = null) => {
  const response = await api.post('/story/publish', {
    vehicle_id: vehicleId,
    event_type: eventType,
    platform,
    caption,
  });
  return response.data;
};

/**
 * Generate feed preview for Settings page
 * Shows demo image with crop + watermark applied
 * @param {string} platform - 'instagram' or 'facebook'
 * @returns {Promise<Object>} { preview_url, crop_format, watermark_enabled }
 */
export const generateFeedPreview = async (platform = 'instagram') => {
  const response = await api.get(`/story/preview-feed/${platform}`);
  return response.data;
};

/**
 * Get template thumbnail URLs for an event type
 * Thumbnails are cached server-side at low resolution
 * @param {string} eventType - 'new_vehicle', 'price_reduction', 'sold_vehicle'
 * @returns {Promise<Object>} { thumbnails: { templateId: url, ... } }
 */
export const getTemplateThumbnails = async (eventType = 'new_vehicle') => {
  const response = await api.get(`/story/template-thumbnails/${eventType}`);
  return response.data;
};

/**
 * Migrate legacy settings to new format
 * @returns {Promise<Object>} { migrated: boolean }
 */
export const migrateStorySettings = async () => {
  const response = await api.post('/story/migrate');
  return response.data;
};

// Default export for convenience
const storyApi = {
  getConfig: getStoryConfig,
  updateConfig: updateStoryConfig,
  getAllConfigs: getAllStoryConfigs,
  getTemplates: getStoryTemplates,
  preview: generateStoryPreview,
  demoPreview: generateDemoPreview,
  publish: publishStory,
  feedPreview: generateFeedPreview,
  templateThumbnails: getTemplateThumbnails,
  migrate: migrateStorySettings,
};
export default storyApi;
